import { useEffect, useId, useRef } from "react"
import type { ReactNode } from "react"
import styles from "@/styles/components/Modal.module.css"

interface ModalProps {
  title: string
  description?: string
  /**
   * The chip left of the title. Decorative: the title already says what the
   * dialog is, so it is `aria-hidden` and nothing announces it.
   */
  icon?: ReactNode
  width?: "default" | "narrow"
  dismissible?: boolean
  onClose: () => void
  actions: ReactNode
  children: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default function Modal({
  title,
  description,
  icon,
  width = "default",
  dismissible = false,
  onClose,
  actions,
  children,
}: ModalProps) {
  const id = useId()
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissible) onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [dismissible, onClose])

  /*
   * `aria-modal` is a promise to assistive technology, not a behaviour: the
   * browser still tabs straight out of the panel and into the round underneath,
   * where the scrim hides what has focus — a facilitator typing into a pain
   * point they cannot see. So the cycle is closed by hand.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !panel.current) return

      const stops = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      const first = stops[0]
      const last = stops[stops.length - 1]
      if (!first || !last) {
        // Nothing to land on; keep focus on the panel rather than losing it.
        event.preventDefault()
        panel.current.focus()
        return
      }

      const active = document.activeElement
      if (event.shiftKey && (active === first || active === panel.current)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [])

  // Focus goes back where it came from, or the next Tab restarts at the top of
  // the page instead of beside the button that opened this.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    panel.current?.focus()
    return () => opener?.focus?.()
  }, [])

  return (
    <div
      className={dismissible ? `${styles.scrim} ${styles.scrimDim}` : styles.scrim}
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className={width === "narrow" ? `${styles.dialog} ${styles.narrow}` : styles.dialog}
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-description` : undefined}
      >
        <div className={styles.head}>
          {icon ? (
            <span className={styles.icon} aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <div className={styles.headText}>
            <h2 className={styles.title} id={`${id}-title`}>
              {title}
            </h2>
            {description ? (
              <p className={styles.description} id={`${id}-description`}>
                {description}
              </p>
            ) : null}
          </div>
          {dismissible ? (
            <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M1.5 1.5L12.5 12.5M12.5 1.5L1.5 12.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
        </div>

        <div className={styles.body}>{children}</div>

        <div className={styles.foot}>
          <div className={styles.footActions}>{actions}</div>
        </div>
      </div>
    </div>
  )
}
